import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Zap, ArrowRight, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DemoConversionNudge from "@/components/DemoConversionNudge";

/**
 * Demo banner for authenticated demo users.
 *
 * Shows:
 *  - "You are in a live demo — expires in Nd Xh"
 *  - A one-click "Run a sample D1 delivery" CTA that hits
 *    POST /api/demo/quick-deliver and refreshes the dashboard so the
 *    delivered order appears in real time.
 *  - Upgrade CTA to /pricing after the first delivery.
 *
 * Hidden entirely if the user is not a demo account (returns null).
 */
export default function DemoBanner({ onDelivered }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [delivered, setDelivered] = useState(null);
  // Ticks up each time a delivery succeeds — used as the trigger key for the
  // conversion nudge so it can arm its 15s timer on the FIRST successful
  // quick-deliver of this session.
  const [firstDeliveryToken, setFirstDeliveryToken] = useState(null);

  useEffect(() => {
    if (!user?.demo) return;
    api.get("/demo/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, [user?.demo]);

  if (!user?.demo) return null;

  const runDemo = async () => {
    setErr("");
    setMsg("");
    setBusy(true);
    try {
      const { data } = await api.post("/demo/quick-deliver");
      if (data?.delivery?.delivered) {
        setDelivered(data.order);
        setMsg("Your SIMULATOR delivery is ready — check the deliverables tab.");
        // Arm the 15s conversion nudge on the FIRST successful delivery only.
        // DemoConversionNudge itself uses a localStorage flag to prevent re-fire.
        setFirstDeliveryToken((t) => t ?? Date.now());
      } else {
        setMsg(`Order staged for human review: ${data?.delivery?.reason || "unknown"}`);
      }
      onDelivered?.(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const remaining = status?.seconds_remaining;
  let timeStr = "";
  if (remaining != null) {
    const days = Math.floor(remaining / 86400);
    const hours = Math.floor((remaining % 86400) / 3600);
    timeStr = days > 0 ? `${days}d ${hours}h remaining` : `${hours}h remaining`;
  }

  return (
    <div
      className="dt6-card p-6 mb-6"
      style={{
        background: "linear-gradient(120deg, rgba(46,124,246,0.12), rgba(46,124,246,0.02))",
        borderColor: "var(--dt6-primary)",
      }}
      data-testid="demo-banner"
    >
      <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
        <div className="flex-shrink-0 text-[color:var(--dt6-primary)]">
          <Sparkles size={28} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="dt6-chip dt6-chip-primary mb-2" data-testid="demo-banner-badge">
            / LIVE DEMO SESSION{timeStr && ` · ${timeStr}`}
          </div>
          <h3 className="font-heading text-xl font-bold">
            {delivered
              ? "SIX delivered your file. Check the Deliverables tab."
              : "Click the button. Watch SIX auto-deliver a sample file in seconds."}
          </h3>
          <p className="text-sm text-[color:var(--dt6-muted)] mt-2">
            <ShieldCheck size={12} className="inline-block mr-1" />
            Everything you'll see is SIMULATOR output — synthetic, labeled, and never real voter data.
            The pipeline is the exact same code that runs against live L2/D1 for real clients.
          </p>
          {msg && (
            <div
              className="mt-3 text-sm text-[color:var(--dt6-success)]"
              data-testid="demo-banner-msg"
            >
              {msg}
            </div>
          )}
          {err && (
            <div
              className="mt-3 text-sm text-[color:var(--dt6-danger)]"
              data-testid="demo-banner-error"
            >
              {err}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <button
            onClick={runDemo}
            disabled={busy}
            className="dt6-btn dt6-btn-primary"
            data-testid="demo-banner-run-btn"
          >
            {busy ? "DELIVERING..." : (
              <>
                <Zap size={14} /> {delivered ? "Run another sample" : "Run sample delivery"}
              </>
            )}
          </button>
          <Link
            to="/pricing"
            className="dt6-btn text-xs justify-center"
            data-testid="demo-banner-pricing-cta"
          >
            See real pricing <ArrowRight size={12} />
          </Link>
        </div>
      </div>
      <DemoConversionNudge triggerKey={firstDeliveryToken} />
    </div>
  );
}
